import type { ColumnDef } from '@tanstack/vue-table';
import { h } from 'vue';

import type { Call } from '@stuut/api/models';
import { CallProvider, CallStatus } from '@stuut/api/models';
import { formatDuration, formatPhoneNumber, humanize } from '@stuut/common/src/utils';
import { Shimmer } from '@ui/components/ai-elements/shimmer';
import { DirectionBadge } from '@ui/components/badge';
import { DataTableColumnHeader } from '@ui/components/datatable';
import { Timestamp } from '@ui/components/time';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

import CallSentimentIcon from '@/components/calls/CallSentimentIcon.vue';
import CallStatusBadge from '@/components/calls/CallStatusBadge.vue';
import { PartnerQuickView } from '@/components/partners';
import CallTaskIndicator from '@/components/tasks/CallTaskIndicator.vue';
import { UserAvatar } from '@/components/users';
import type { CallProcessingStep } from '@/types/callProcessing';
import { getCallProcessingLabel, resolveCallProcessingStep } from '@/types/callProcessing';

export function createColumns(
  processingSteps?: Map<string, CallProcessingStep>,
  showProvider = false,
): ColumnDef<Call>[] {
  const getStep = (call: Call) => resolveCallProcessingStep(call, processingSteps);

  const providerColumn: ColumnDef<Call> = {
    accessorKey: 'provider',
    cell: ({ row }) => {
      const step = getStep(row.original);
      const provider =
        row.original.provider === CallProvider.LIVEKIT
          ? 'LiveKit'
          : row.original.provider === CallProvider.RETELL
            ? 'Retell'
            : '--';
      if (step) {
        return h(Shimmer, { as: 'span', duration: 1.5, class: 'text-xs' }, () => provider);
      }
      return provider;
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Provider' }),
    size: 90,
  };

  const columns: ColumnDef<Call>[] = [
    {
      accessorKey: 'direction',
      cell: ({ row }) => {
        return h(DirectionBadge, {
          direction: row.original.direction,
        });
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) => h(DataTableColumnHeader, { column, title: '' }),
      size: 50,
    },
    {
      accessorKey: 'start_time',
      cell: ({ row }) => {
        const step = getStep(row.original);
        const time = row.original.start_time || row.original.created_at;

        if (step) {
          return h(Shimmer, { as: 'div', duration: 1.5 }, () =>
            h(CallStatusBadge, {
              status: row.original.status,
              scheduledFor: row.original.workflow_run?.scheduled_for,
            }),
          );
        }

        return h(Timestamp, {
          format: 'L h:mm A',
          local: true,
          showFromNow: true,
          time,
          withConversionTable: true,
        });
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Date' }),
      size: 130,
    },
    {
      accessorKey: 'partner',
      cell: ({ row }) => {
        const step = getStep(row.original);

        if (step && row.original.partner) {
          return h(
            Shimmer,
            { as: 'span', duration: 1.5, class: 'text-xs' },
            () => row.original.partner!.name || '--',
          );
        }

        return row.original.partner ? h(PartnerQuickView, { partner: row.original.partner }) : '--';
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Customer' }),
      size: 120,
    },
    {
      accessorKey: 'to_number',
      cell: ({ row }) => {
        const phoneNumber = row.original.to_number;
        if (!phoneNumber) return '--';
        const formattedNumber = formatPhoneNumber(phoneNumber);
        const step = getStep(row.original);

        if (step) {
          return h('div', { class: 'flex justify-center items-center' }, [
            h(
              Shimmer,
              { as: 'span', duration: 1.5, class: 'text-xs' },
              () => formattedNumber || '--',
            ),
          ]);
        }

        return h('div', { class: 'flex justify-center items-center' }, [
          formattedNumber ? h('span', formattedNumber) : '--',
        ]);
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) =>
        h(DataTableColumnHeader, {
          class: 'text-center',
          column,
          title: 'Phone',
        }),
      size: 120,
    },
    {
      accessorKey: 'duration',
      cell: ({ row }) => {
        const step = getStep(row.original);
        const startTime =
          row.original.start_time && row.original.status === CallStatus.COMPLETED
            ? formatDuration(row.original.duration)
            : '--';

        if (step) {
          return h('div', { class: 'flex justify-center items-center' }, [
            h(Shimmer, { as: 'span', duration: 1.5, class: 'text-xs' }, () => startTime || '--'),
          ]);
        }

        return h('div', { class: 'flex justify-center items-center' }, [
          h('span', startTime || '--'),
        ]);
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) =>
        h(DataTableColumnHeader, {
          class: 'text-center',
          column,
          title: 'Duration',
        }),
      meta: {
        class: 'border-l',
      },
      size: 80,
    },
    {
      accessorKey: 'in_voicemail',
      cell: ({ row }) => {
        if (!row.original.in_voicemail) {
          return h('div', { class: 'flex justify-center items-center' }, '--');
        }
        const left = row.original.disconnection_reason === 'voicemail_left';
        return h('div', { class: 'flex justify-center items-center' }, [
          h('i', {
            class: left ? 'fas fa-check text-success' : 'fas fa-close text-muted-foreground',
          }),
        ]);
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) =>
        h('div', { class: 'flex items-center justify-center gap-1' }, [
          h(DataTableColumnHeader, { column, title: 'Voicemail' }),
          h(
            Tooltip,
            {},
            {
              default: () => [
                h(
                  TooltipTrigger,
                  { asChild: true },
                  {
                    default: () =>
                      h('i', {
                        class: 'fas fa-circle-info text-muted-foreground text-xs cursor-default',
                      }),
                  },
                ),
                h(
                  TooltipContent,
                  {},
                  {
                    default: () =>
                      'Detection is based on transcript analysis and may not be accurate for non-English calls.',
                  },
                ),
              ],
            },
          ),
        ]),
      size: 80,
    },
    {
      accessorKey: 'sentiment',
      cell: ({ row }) => {
        return h('div', { class: 'flex justify-center items-center' }, [
          row.original.sentiment
            ? h(CallSentimentIcon, { sentiment: row.original.sentiment })
            : '--',
        ]);
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) =>
        h(DataTableColumnHeader, {
          class: 'text-center',
          column,
          title: 'Sentiment',
        }),
      size: 80,
    },
    {
      accessorKey: 'classifications',
      cell: ({ row }) =>
        h(Tooltip, [
          h(
            TooltipTrigger,
            { class: 'text-2xs' },
            row.original?.classifications?.map((c) => humanize(c.classification))?.join(', ') ||
              '-',
          ),
          h(
            TooltipContent,
            { side: 'left' },
            row.original?.classifications?.map((c) => humanize(c.classification))?.join(', ') ||
              '-',
          ),
        ]),
      enableHiding: false,
      enableSorting: false,

      header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Categories' }),
      size: 140,
    },
    {
      accessorKey: 'openTasks',
      cell: ({ row }) => {
        return h(
          'div',
          { class: 'flex justify-center items-center' },
          h(CallTaskIndicator, {
            call: row?.original,
          }),
        );
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) =>
        h(DataTableColumnHeader, {
          class: 'text-center',
          column,
          title: 'Open Tasks',
        }),
      size: 90,
    },
    {
      accessorKey: 'approver',
      cell: ({ row }) => {
        if (row.original.approver?.id) {
          return h(UserAvatar, {
            class: 'size-7',
            size: 'sm',
            tooltipText: row.original.approver.name,
            user: row.original.approver,
          });
        }

        return h(
          'div',
          { class: 'text-xs', title: row.original.workflow?.name || '' },
          row.original.workflow?.name || '--',
        );
      },
      enableHiding: false,

      enableSorting: false,
      header: ({ column }) =>
        h(DataTableColumnHeader, {
          column,
          title: 'Approver',
        }),
      size: 150,
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => {
        const step = getStep(row.original);

        if (step) {
          return h('div', { class: 'flex justify-center items-center gap-x-1.5' }, [
            h(
              Shimmer,
              { as: 'span', duration: 1.5, class: 'text-xs font-semibold' },
              () => getCallProcessingLabel(step) || row.original.status,
            ),
          ]);
        }

        return h('div', { class: 'flex justify-center items-center' }, [
          row.original.status
            ? h(CallStatusBadge, {
                scheduledFor: row.original.workflow_run?.scheduled_for,
                status: row.original.status,
              })
            : '--',
        ]);
      },
      enableHiding: false,
      enableSorting: false,
      header: ({ column }) =>
        h(DataTableColumnHeader, {
          class: 'text-center',
          column,
          title: 'Status',
        }),
      meta: {
        class: 'border-l',
      },
      size: 140,
    },
  ];

  if (showProvider) {
    const insertAfter = columns.findIndex(
      (c) => 'accessorKey' in c && c.accessorKey === 'start_time',
    );
    if (insertAfter === -1) columns.push(providerColumn);
    else columns.splice(insertAfter + 1, 0, providerColumn);
  }

  return columns;
}
